/** Blocs cartes : orientation, comparatif, frise, patrimoine, atouts, formules, témoignages, FAQ, Lancelot. */
import React from "react";
import { Helmet } from "react-helmet-async";
import { Check } from "lucide-react";
import { CERTITUDE_LABEL, FEATURE_ICONS } from "./shared";

export function OrientationCardsBlock({ d, t, abs }) {
  const cards = (d.cards || []).filter((c) => c.title);
  return (
    <section className="py-20 border-t border-pierre/60">
      {d.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or text-center mb-3">{t(d.eyebrow)}</div>}
      {d.title && <h2 className="text-3xl md:text-5xl font-serif text-domaine text-center">{t(d.title)}</h2>}
      {d.subtitle && <p className="mt-4 text-lg text-ardoise/80 text-center max-w-2xl mx-auto">{t(d.subtitle)}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
        {cards.map((c, i) => (
          <a key={`orient-${i}`} href={c.link || "#"}
             className="group bg-white border border-pierre overflow-hidden flex flex-col hover:border-or transition-colors">
            {c.image_url && (
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={abs(c.image_url)} alt={c.image_alt || c.title}
                     loading="lazy"
                     className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            )}
            <div className="p-7 md:p-8 flex-1 flex flex-col">
              {c.eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] text-or mb-2">{c.eyebrow}</div>}
              <h3 className="text-2xl md:text-3xl font-serif text-domaine">{c.title}</h3>
              {c.description && <p className="mt-2 text-sm text-ardoise/75 leading-relaxed max-w-md">{c.description}</p>}
              {c.cta_label && (
                <span className="inline-block mt-5 self-start text-xs uppercase tracking-[0.2em] text-domaine border-b border-or pb-1 group-hover:text-or transition-colors">
                  {c.cta_label} →
                </span>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export function ComparisonTableBlock({ d, t }) {
  const cols = (d.columns || []).filter((c) => c.name);
  const rows = (d.rows || []);
  return (
    <section className="py-16">
      {d.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or text-center mb-2">{t(d.eyebrow)}</div>}
      {d.title && <h2 className="text-2xl md:text-3xl font-serif text-domaine text-center">{t(d.title)}</h2>}
      {d.subtitle && <p className="text-center text-ardoise/70 mt-3 max-w-2xl mx-auto">{t(d.subtitle)}</p>}
      {/* Table version — desktop */}
      <div className="hidden md:block mt-10 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="text-left p-3 text-[10px] uppercase tracking-widest text-or">Critère</th>
              {cols.map((c, i) => (
                <th key={`col-${i}`} className="p-3 text-left font-serif text-lg text-domaine border-b border-pierre">
                  {c.name}
                  {c.link && (
                    <a href={c.link} className="block text-[10px] uppercase tracking-widest text-or mt-1 hover:text-domaine">Découvrir →</a>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={`row-${ri}`} className="border-b border-pierre/40">
                <td className="p-3 text-ardoise/70 text-xs uppercase tracking-widest align-top">{t(r.label)}</td>
                {cols.map((c, ci) => (
                  <td key={`cell-${ri}-${ci}`} className="p-3 text-ardoise align-top">
                    {r.values?.[ci] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Cards version — mobile */}
      <div className="md:hidden mt-8 space-y-6">
        {cols.map((c, ci) => (
          <div key={`mc-${ci}`} className="border border-pierre bg-white p-5">
            <h3 className="font-serif text-xl text-domaine">{c.name}</h3>
            {c.link && (
              <a href={c.link} className="block text-[10px] uppercase tracking-widest text-or mt-1 mb-3">Découvrir →</a>
            )}
            <dl className="grid grid-cols-2 gap-y-2 gap-x-3 text-sm mt-3">
              {rows.map((r, ri) => (
                <React.Fragment key={`mrow-${ci}-${ri}`}>
                  <dt className="text-ardoise/60 text-xs">{t(r.label)}</dt>
                  <dd className="text-ardoise">{r.values?.[ci] ?? "—"}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}

export function TimelineBlock({ d, t }) {
  const entries = (d.entries || []).filter((e) => e.title || e.date_label || e.label);
  return (
    <section className="py-16 md:py-24">
      {d.title && <h2 className="text-3xl md:text-4xl font-serif text-domaine text-center mb-3">{t(d.title)}</h2>}
      {d.subtitle && <p className="text-center text-ardoise/70 mb-12 max-w-2xl mx-auto">{t(d.subtitle)}</p>}
      <ol className="relative border-l-2 border-or/30 ml-4 md:ml-8 space-y-10">
        {entries.map((e, i) => (
          <li key={`tl-${i}`} className="pl-6 md:pl-10 relative">
            <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-or ring-4 ring-lin" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-or">{e.date_label || e.label}</div>
            {e.title && (
              e.anchor
                ? <a href={`#${e.anchor}`} className="block text-xl md:text-2xl font-serif text-domaine mt-1 hover:text-or transition-colors">{t(e.title)}</a>
                : <h3 className="text-xl md:text-2xl font-serif text-domaine mt-1">{t(e.title)}</h3>
            )}
            {e.description && <p className="text-sm text-ardoise/80 mt-2 max-w-2xl">{t(e.description)}</p>}
            {e.certitude && e.certitude !== "documented" && (
              <span className="inline-block mt-2 text-[9px] uppercase tracking-widest px-2 py-0.5 border border-or/40 text-or/80 bg-or/5">
                {CERTITUDE_LABEL[e.certitude] || e.certitude}
              </span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function PatrimonyCardsBlock({ d, t, abs }) {
  const cards = (d.cards || []).filter((c) => c.title);
  const renderInner = (c) => (
    <>
      {c.image_url && (
        <img src={abs(c.image_url)} alt={c.image_alt || c.title} loading="lazy"
             className="w-full aspect-[4/3] object-cover border-b border-pierre group-hover:brightness-105 transition" />
      )}
      <div className="p-5 flex flex-col flex-1">
        {c.eyebrow && <div className="text-[10px] uppercase tracking-[0.25em] text-or mb-1">{c.eyebrow}</div>}
        <h3 className="text-xl font-serif text-domaine group-hover:text-or transition-colors">{t(c.title)}</h3>
        {c.description && (
          <p className="text-sm text-ardoise/80 mt-2 flex-1">{t(c.description)}</p>
        )}
        {c.mention && (
          <p className="text-[11px] italic text-ardoise/50 mt-3">{t(c.mention)}</p>
        )}
        {c.certitude && (
          <span className="inline-block mt-3 text-[9px] uppercase tracking-widest px-2 py-0.5 border border-or/40 text-or/80 bg-or/5 self-start">
            {CERTITUDE_LABEL[c.certitude] || c.certitude}
          </span>
        )}
        {c.link && (
          <span className="mt-3 text-[10px] uppercase tracking-widest text-or opacity-0 group-hover:opacity-100 transition-opacity">
            Découvrir →
          </span>
        )}
      </div>
    </>
  );
  return (
    <section className="py-16">
      {d.title && <h2 className="text-2xl md:text-3xl font-serif text-domaine mb-2">{t(d.title)}</h2>}
      {d.subtitle && <p className="text-ardoise/70 mb-8">{t(d.subtitle)}</p>}
      <div className={`grid gap-5 ${
        cards.length === 4 ? "grid-cols-1 md:grid-cols-2"
        : cards.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto"
        : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
      }`}>
        {cards.map((c, i) => {
          const cls = "group border border-pierre bg-white flex flex-col overflow-hidden";
          if (c.link) {
            const isExternal = /^https?:\/\//.test(c.link);
            return (
              <a key={`pat-${i}`} href={c.link}
                 target={isExternal ? "_blank" : undefined}
                 rel={isExternal ? "noopener noreferrer" : undefined}
                 data-testid={`patrimony-card-${i}`}
                 className={`${cls} hover:shadow-lg transition-shadow cursor-pointer`}>
                {renderInner(c)}
              </a>
            );
          }
          return (
            <article key={`pat-${i}`} className={cls} data-testid={`patrimony-card-${i}`}>
              {renderInner(c)}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function FeatureGridBlock({ d, t }) {
  const items = (d.items || []).filter((it) => it.title);
  return (
    <section className="py-16">
      {d.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or text-center mb-2">{t(d.eyebrow)}</div>}
      {d.title && <h2 className="text-3xl md:text-4xl font-serif text-domaine text-center">{t(d.title)}</h2>}
      {d.subtitle && <p className="text-center text-ardoise/70 mt-3 max-w-2xl mx-auto">{t(d.subtitle)}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {items.map((it, i) => {
          const FeatIcon = FEATURE_ICONS[it.icon];
          return (
            <div key={`feat-${it.title}-${i}`} className="border border-pierre bg-white p-6 hover:border-or transition-colors"
                 data-testid={`feature-item-${i}`}>
              {FeatIcon
                ? <FeatIcon size={30} strokeWidth={1.4} className="text-or" aria-hidden="true" />
                : <div className="text-or font-serif text-2xl">{String(i + 1).padStart(2, "0")}</div>}
              <h3 className="font-serif text-lg text-domaine mt-3">{t(it.title)}</h3>
              {it.text && <p className="text-sm text-ardoise/75 mt-2">{t(it.text)}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function PricingTiersBlock({ d, t }) {
  const tiers = (d.tiers || []).filter((tr) => tr.name);
  return (
    <section className="py-16">
      {d.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or text-center mb-2">{t(d.eyebrow)}</div>}
      {d.title && <h2 className="text-3xl md:text-4xl font-serif text-domaine text-center">{t(d.title)}</h2>}
      {d.subtitle && <p className="text-center text-ardoise/70 mt-3 max-w-2xl mx-auto">{t(d.subtitle)}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 items-stretch">
        {tiers.map((tr, i) => {
          const hi = !!tr.highlight;
          const rawPrice = t(tr.price || "");
          const priceParts = rawPrice.match(/^(.*?)([\d\s.,\u00a0\u202f]+\s*€|Sur devis)$/i);
          const pricePrefix = priceParts ? priceParts[1].trim() : "";
          const priceMain = priceParts ? priceParts[2].trim() : rawPrice;
          return (
            <div key={`tier-${tr.name}-${i}`}
                 data-testid={`pricing-tier-${i}`}
                 className={`relative flex flex-col transition-transform duration-300 ${
                   hi
                     ? "bg-domaine text-lin border border-or shadow-2xl md:scale-105 md:-my-2 z-10"
                     : "bg-white border border-pierre hover:border-or hover:-translate-y-1 hover:shadow-lg"}`}>
              {hi && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-or text-domaine text-[10px] font-semibold uppercase tracking-[0.25em] px-4 py-1.5 whitespace-nowrap shadow">
                  ★ {tr.highlight_label || "Notre formule phare"}
                </div>
              )}
              <div className={`px-8 pt-10 pb-6 text-center border-b ${hi ? "border-lin/15" : "border-pierre/60"}`}>
                <h3 style={{ color: "inherit" }} className={`font-serif text-2xl tracking-wide ${hi ? "text-lin" : "text-domaine"}`}>{tr.name}</h3>
                {tr.tagline && <p className={`text-sm mt-3 leading-relaxed ${hi ? "text-lin/75" : "text-ardoise/70"}`}>{t(tr.tagline)}</p>}
                {tr.price && (
                  <div className="mt-6">
                    {pricePrefix && <div className={`text-[11px] uppercase tracking-[0.2em] ${hi ? "text-lin/60" : "text-ardoise/50"}`}>{pricePrefix}</div>}
                    <div className={`font-serif text-4xl mt-1 ${hi ? "text-or" : "text-domaine"}`}>{priceMain}</div>
                  </div>
                )}
                <div className={`mx-auto mt-6 h-px w-12 ${hi ? "bg-or/70" : "bg-or/50"}`} />
              </div>
              <ul className="px-8 py-6 space-y-3 flex-1">
                {(tr.items || []).filter(Boolean).map((li, j) => (
                  <li key={`ti-${i}-${j}`} className={`text-sm flex items-start gap-3 ${hi ? "text-lin/90" : "text-ardoise"}`}>
                    <Check size={15} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${hi ? "text-or" : "text-or"}`} aria-hidden="true" />
                    <span>{t(li)}</span>
                  </li>
                ))}
              </ul>
              <div className="px-8 pb-8">
                {tr.footnote && <p className={`text-[11px] italic mb-4 ${hi ? "text-lin/50" : "text-ardoise/50"}`}>{t(tr.footnote)}</p>}
                <a href={tr.link || "/nous-contacter"}
                   className={`block text-center px-6 py-3.5 text-xs uppercase tracking-[0.25em] transition-colors ${
                     hi
                       ? "bg-or text-domaine font-semibold hover:bg-lin"
                       : "border border-domaine text-domaine hover:bg-domaine hover:text-lin"}`}>
                  {tr.cta_label || "Demander un devis"}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function TestimonialsBlock({ d, t, abs }) {
  const items = (d.items || []).filter((it) => it.text);
  return (
    <section className="py-16">
      {d.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or text-center mb-2">{t(d.eyebrow)}</div>}
      {d.title && <h2 className="text-3xl md:text-4xl font-serif text-domaine text-center mb-10">{t(d.title)}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((it, i) => (
          <figure key={`temo-${i}-${(it.author || "").slice(0, 12)}`} className="border border-pierre bg-white flex flex-col sm:flex-row overflow-hidden"
                  data-testid={`testimonial-${i}`}>
            {it.image_url && (
              <img src={abs(it.image_url)} alt={it.author || ""} loading="lazy"
                   className="w-full h-56 sm:h-auto sm:w-44 md:w-52 shrink-0 object-cover" />
            )}
            <div className="p-6 flex flex-col flex-1">
              <div className="text-or text-4xl font-serif leading-none select-none">“</div>
              {it.title && <p className="font-serif text-lg text-domaine mt-1">{t(it.title)}</p>}
              <blockquote className="text-sm text-ardoise/80 mt-3 leading-relaxed flex-1 whitespace-pre-wrap">{t(it.text)}</blockquote>
              {it.author && (
                <figcaption className="mt-4 text-xs uppercase tracking-[0.2em] text-or">— {it.author}</figcaption>
              )}
            </div>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function TestimonialsEditorialBlock({ d, t }) {
  const items = (d.items || []).filter((it) => it.text);
  if (items.length === 0) return null;
  const cols = items.length === 1 ? "md:grid-cols-1 max-w-2xl" : items.length === 2 ? "md:grid-cols-2 max-w-4xl" : "md:grid-cols-3 max-w-7xl";
  return (
    <section className="py-20 md:py-28" data-testid="testimonials-editorial">
      <div className="text-center mb-14 md:mb-16">
        {d.eyebrow && <p className="text-[11px] uppercase tracking-[0.3em] text-or mb-3">{t(d.eyebrow)}</p>}
        {d.title && <h2 className="font-serif text-3xl md:text-4xl text-domaine">{t(d.title)}</h2>}
        {d.subtitle && <p className="mt-4 text-ardoise/70 max-w-2xl mx-auto">{t(d.subtitle)}</p>}
      </div>
      <div className={`grid grid-cols-1 ${cols} gap-12 lg:gap-16 mx-auto`}>
        {items.map((it, i) => (
          <figure key={`temo-ed-${i}`} className="relative flex flex-col border-l border-pierre pl-6 py-2"
                  data-testid={`testimonial-editorial-${i}`}>
            <span aria-hidden="true" className="absolute -top-5 -left-3 text-6xl font-serif text-pierre/50 leading-none select-none">“</span>
            <blockquote className="relative z-10 font-serif text-xl lg:text-2xl text-ardoise leading-relaxed italic mb-8 whitespace-pre-wrap">
              {t(it.text)}
            </blockquote>
            <figcaption className="mt-auto">
              {it.author && <p className="uppercase tracking-widest text-[11px] font-semibold text-or">{it.author}</p>}
              {(it.context || it.title) && <p className="text-xs text-ardoise/60 mt-1">{t(it.context || it.title)}</p>}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function LancelotPromptBlock({ d, t }) {
  const questions = (d.suggested_questions || []).filter(Boolean);
  return (
    <section className="py-16">
      <div className="border border-or/50 bg-or/5 p-8 md:p-12">
        {d.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or mb-2">{t(d.eyebrow)}</div>}
        {d.title && <h2 className="text-2xl md:text-3xl font-serif text-domaine">{t(d.title)}</h2>}
        {d.description && <p className="text-ardoise/80 mt-3 max-w-2xl">{t(d.description)}</p>}
        {questions.length > 0 && (
          <ul className="mt-6 space-y-2 text-sm text-ardoise/80">
            {questions.map((q, i) => (
              <li key={`lq-${i}`} className="flex gap-2">
                <span className="text-or">•</span>
                <span className="italic">« {t(q)} »</span>
              </li>
            ))}
          </ul>
        )}
        <button type="button" disabled
                className="mt-8 inline-block px-8 py-3 bg-domaine/60 text-lin text-xs uppercase tracking-[0.2em] cursor-not-allowed">
          {t(d.cta_label || "Demander à Lancelot")} — Bientôt disponible
        </button>
      </div>
    </section>
  );
}

export function FaqBlock({ d }) {
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (d.items || []).filter((it) => it.q && it.a).map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
  return (
    <div className="space-y-3">
      {faqLd.mainEntity.length > 0 && (
        <Helmet><script type="application/ld+json">{JSON.stringify(faqLd)}</script></Helmet>
      )}
      {(d.items || []).map((it, i) => (
        <details key={`faq-${i}-${(it.q || '').slice(0, 20)}`} className="border border-pierre bg-lin p-4 group">
          <summary className="font-serif text-lg text-domaine cursor-pointer">{it.q}</summary>
          <p className="mt-3 text-sm text-ardoise whitespace-pre-wrap">{it.a}</p>
        </details>
      ))}
    </div>
  );
}
